import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Camera, ChevronDown, Filter as FilterIcon, X } from "lucide-react";
import Breadcrumb from "@/components/site/Breadcrumb";
import SEO from "@/components/site/SEO";
import { api } from "@/lib/api";
import { PRODUCT_CATEGORIES, categoryName } from "@/lib/categories";
import { buildBreadcrumbs } from "@/lib/breadcrumbs";

const PAGE_SIZE = 12;
const SORTS = [
  { key: "name-asc", label: "Product Name: A to Z" },
  { key: "name-desc", label: "Product Name: Z to A" },
  { key: "category", label: "Category" },
];

function ProductCard({ p }) {
  const v = "analytical-v1";
  const bust = (src) => (src ? `${src}${src.includes("?") ? "&" : "?"}v=${v}` : src);
  return (
    <Link
      to={`/medical-laboratory/${p.slug}`}
      data-testid={`product-card-${p.slug}`}
      aria-label={`View details for ${p.name}`}
      className="group flex flex-col bg-white border border-black/[0.04] rounded-xl overflow-hidden transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-[0_20px_40px_-15px_rgba(16,29,54,0.12)] hover:border-black/[0.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mist-gold focus-visible:ring-offset-2"
    >
      <div className="relative aspect-square bg-white flex items-center justify-center p-6 md:p-8">
        {p.image ? (
          <picture>
            {p.image_fallback && <source srcSet={bust(p.image)} type="image/webp" />}
            <img
              src={bust(p.image_fallback || p.image)}
              alt={p.name}
              data-testid="product-image"
              loading="lazy"
              decoding="async"
              className="w-full h-full object-contain transition-transform duration-500 ease-out group-hover:scale-[1.03]"
            />
          </picture>
        ) : (
          <div className="flex flex-col items-center gap-2 text-mist-navy/40">
            <Camera className="h-8 w-8" strokeWidth={1.5} aria-hidden />
            <span className="text-xs">Image coming soon</span>
          </div>
        )}
      </div>
      <div className="px-6 py-4 border-t border-black/[0.04]">
        <div
          className="font-semibold text-mist-navy text-[15px] md:text-base leading-snug tracking-tight line-clamp-2 min-h-[2.6em]"
          title={p.name}
        >
          {p.name}
        </div>
        <div className="mt-1 text-xs uppercase tracking-[0.12em] text-mist-navy/50">
          {categoryName(p.category)}
        </div>
        <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-mist-navy group-hover:underline underline-offset-4 transition-all duration-200">
          View details
          <span aria-hidden className="inline-block transition-transform duration-200 group-hover:translate-x-1">→</span>
        </span>
      </div>
    </Link>
  );
}

function Skeleton() {
  return (
    <div className="animate-pulse flex flex-col bg-white border border-black/[0.06] rounded-xl overflow-hidden">
      <div className="aspect-square bg-mist-off" />
      <div className="px-6 py-5">
        <div className="h-4 bg-mist-off rounded w-3/4" />
        <div className="h-3 bg-mist-off rounded mt-2 w-1/2" />
        <div className="h-3 bg-mist-off rounded mt-4 w-24" />
      </div>
    </div>
  );
}

function CategoryButton({ active, label, count, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      data-testid={testid}
      className={`w-full flex items-center justify-between py-2.5 pr-2 pl-3 rounded-sm transition-colors duration-200 ease-out text-[14px] ${
        active
          ? "font-semibold text-mist-navy"
          : "font-medium text-mist-navy/70 hover:text-mist-navy"
      }`}
    >
      <span>{label}</span>
      <span className="text-xs text-mist-navy/40 font-normal">({count})</span>
    </button>
  );
}

export default function MedicalLaboratoryPage() {
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") || "all";
  const sort = searchParams.get("sort") || "name-asc";

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [categoriesData, setCategoriesData] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const breadcrumbs = buildBreadcrumbs(pathname);

  const fetchList = useCallback(async (offset, replace) => {
    const params = { sort, limit: PAGE_SIZE, offset };
    if (category !== "all") params.category = category;
    const { data } = await api.get("/products", { params });
    if (replace) setItems(data.items);
    else setItems((prev) => [...prev, ...data.items]);
    setTotal(data.total);
  }, [category, sort]);

  useEffect(() => {
    setLoading(true);
    fetchList(0, true).finally(() => setLoading(false));
  }, [fetchList]);

  useEffect(() => {
    api.get("/product-categories").then(({ data }) => setCategoriesData(data)).catch(() => {});
  }, []);

  const setCategory = (slug) => {
    const next = new URLSearchParams(searchParams);
    if (slug === "all") next.delete("category");
    else next.set("category", slug);
    setSearchParams(next, { replace: true });
    setDrawerOpen(false);
  };

  const setSort = (val) => {
    const next = new URLSearchParams(searchParams);
    next.set("sort", val);
    setSearchParams(next, { replace: true });
  };

  const onLoadMore = async () => {
    setLoadingMore(true);
    try { await fetchList(items.length, false); } finally { setLoadingMore(false); }
  };

  const catCountMap = useMemo(() => {
    const m = {};
    categoriesData.forEach((c) => (m[c.slug] = c.count));
    return m;
  }, [categoriesData]);

  const visibleCategories = PRODUCT_CATEGORIES.filter((c) => (catCountMap[c.slug] || 0) > 0);
  const allCount = catCountMap.all ?? total;

  const seo = {
    title: "Medical Laboratory Equipment | MIST",
    description: "Explore MIST's catalog of medical and laboratory equipment.",
  };

  return (
    <>
      <SEO seo={seo} />
      {/* Page banner */}
      <section className="bg-mist-navy text-white pt-32 pb-14 lg:pt-40 lg:pb-16">
        <div className="container-mist">
          {breadcrumbs.length > 0 && <Breadcrumb items={breadcrumbs} />}
          <div className="eyebrow mb-3">Catalog</div>
          <h1
            className="font-display font-bold text-3xl sm:text-4xl lg:text-[44px] leading-[1.1] tracking-[-0.02em] uppercase"
          >
            Medical Lab Equipment
          </h1>
          <p className="mt-6 text-[15px] text-white/80 max-w-xl leading-relaxed">
            Independent supply of medical, laboratory and diagnostic equipment for healthcare, research and educational institutions.
          </p>
        </div>
      </section>

      {/* Catalog body */}
      <section className="bg-mist-warm pt-12 md:pt-16 pb-20">
        <div className="container-mist">
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10 lg:gap-14">
            {/* Sidebar (desktop) */}
            <aside className="hidden lg:block lg:sticky lg:top-28 self-start" aria-label="Product filter">
              <div className="text-[11px] uppercase tracking-[0.2em] font-medium text-mist-navy/50 mb-3">
                Filter by
              </div>
              <div className="font-semibold text-mist-navy text-base mb-4">Category</div>
              <ul className="space-y-1">
                <li>
                  <CategoryButton
                    active={category === "all"}
                    label="All"
                    count={allCount}
                    onClick={() => setCategory("all")}
                    testid="filter-all"
                  />
                </li>
                <li className="border-t border-black/[0.05] my-3" aria-hidden />
                {visibleCategories.map((c) => (
                  <li key={c.slug}>
                    <CategoryButton
                      active={category === c.slug}
                      label={c.name}
                      count={catCountMap[c.slug] || 0}
                      onClick={() => setCategory(c.slug)}
                      testid={`filter-${c.slug}`}
                    />
                  </li>
                ))}
              </ul>
            </aside>

            {/* Right column */}
            <div>
              {/* Toolbar */}
              <div className="flex flex-wrap items-center justify-between gap-3 mb-8 md:mb-10">
                <button
                  type="button"
                  onClick={() => setDrawerOpen(true)}
                  className="lg:hidden inline-flex items-center gap-2 px-4 h-10 rounded-md border border-black/10 bg-white text-sm text-mist-navy hover:border-mist-gold transition-colors"
                  data-testid="mobile-filter-toggle"
                >
                  <FilterIcon className="h-4 w-4" aria-hidden />
                  Filters
                  {category !== "all" && (
                    <span className="ml-1 inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-mist-gold text-mist-navy text-[11px] font-semibold">1</span>
                  )}
                </button>
                <div className="ml-auto flex items-center gap-3">
                  <label htmlFor="sort-select" className="text-xs uppercase tracking-[0.15em] text-mist-navy/50 font-medium">
                    Sort by
                  </label>
                  <div className="relative">
                    <select
                      id="sort-select"
                      data-testid="sort-select"
                      value={sort}
                      onChange={(e) => setSort(e.target.value)}
                      className="appearance-none h-10 pl-3 pr-9 border border-black/10 rounded-md text-sm text-mist-navy bg-white focus:outline-none focus:border-mist-gold focus:ring-2 focus:ring-mist-gold/20 transition-all duration-200"
                    >
                      {SORTS.map((s) => (<option key={s.key} value={s.key}>{s.label}</option>))}
                    </select>
                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-mist-navy/60 pointer-events-none" aria-hidden />
                  </div>
                </div>
              </div>

              {/* Grid */}
              <div
                aria-busy={loading}
                data-testid="product-grid"
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8"
              >
                {loading
                  ? Array.from({ length: PAGE_SIZE }).map((_, i) => <Skeleton key={i} />)
                  : items.length === 0
                    ? (
                      <div className="col-span-full py-20 text-center text-mist-navy/70">
                        <div className="text-lg font-semibold text-mist-navy mb-2">No products in this category yet.</div>
                        <button
                          type="button"
                          onClick={() => setCategory("all")}
                          className="mt-3 inline-flex items-center gap-2 px-5 h-10 rounded-md bg-mist-gold text-mist-navy text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#b8994f] transition-all"
                          data-testid="empty-reset"
                        >
                          Show all products
                        </button>
                      </div>
                    )
                    : items.map((p) => <ProductCard key={p.id || p.slug} p={p} />)
                }
              </div>

              {/* Load more */}
              {!loading && items.length < total && (
                <div className="mt-12 md:mt-16 flex justify-center">
                  <button
                    type="button"
                    onClick={onLoadMore}
                    disabled={loadingMore}
                    data-testid="load-more"
                    className="inline-flex items-center gap-2 px-10 h-12 rounded-md bg-mist-navy text-white text-sm font-semibold uppercase tracking-wider hover:bg-mist-navy/90 transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mist-gold focus-visible:ring-offset-2 disabled:opacity-60"
                  >
                    {loadingMore ? "Loading…" : `Load More (${total - items.length} more)`}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Mobile filter drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setDrawerOpen(false)}>
          <div
            className="absolute inset-y-0 right-0 w-[85%] max-w-sm bg-white overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Filter products"
            data-testid="mobile-filter-drawer"
          >
            <div className="flex items-center justify-between p-5 border-b border-black/[0.05]">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] font-medium text-mist-navy/50 mb-0.5">Filter by</div>
                <div className="font-semibold text-mist-navy text-base">Category</div>
              </div>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close filters"
                className="p-2 -mr-2 text-mist-navy hover:text-mist-gold transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <ul className="p-5 space-y-1">
              <li>
                <CategoryButton
                  active={category === "all"}
                  label="All"
                  count={allCount}
                  onClick={() => setCategory("all")}
                  testid="filter-all-mobile"
                />
              </li>
              <li className="border-t border-black/[0.05] my-3" aria-hidden />
              {visibleCategories.map((c) => (
                <li key={c.slug}>
                  <CategoryButton
                    active={category === c.slug}
                    label={c.name}
                    count={catCountMap[c.slug] || 0}
                    onClick={() => setCategory(c.slug)}
                    testid={`filter-${c.slug}-mobile`}
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}
